import {
  clearValidationForAllParameters, clearValidationForKeys, clearValidationForSingleParameter
} from 'app/area/shared/report/form/validation';
import { Reducer, Action } from 'redux';
import {
  ACTION, ReceiveLanguageOptions,
  RequestExternalUrl, ReceiveExternalUrl, FailExternalUrl,
  RequestDeleteExternalUrl, ReceiveDeleteExternalUrl, FailDeleteExternalUrl,
  RequestSaveExternalUrl, ReceiveSaveExternalUrl, FailSaveExternalUrl,
  UpdateItem, ChangeLanguage, CopyLanguageToAll,
  ClearForm, UpdateValidation, ResetValidation,
  ChangeLongDescription,
  CopyLongDescriptionToAll,
  UpdateExcludedFields
} from './action-types';
import { languageValidationParameter, longDescriptionValidationParameter } from './constants';
import { ExternalUrlAdminFormState as ExternalUrlAdminFormState, ExternalUrlItem } from './types-store';
import { withOptionsReducer } from 'app/components/options-handler';
import { STATE } from 'app/constants';

export const INITIAL_STATE: ExternalUrlAdminFormState = {
  actionType: undefined,
  externalUrlItem: undefined,
  originalExternalUrlItem: undefined,
  fetching: false,
  deleting: false,
  saving: false,
  languageOptions: [],
  validation: {}
};

const reducers: { [indexer: string]: Reducer<ExternalUrlAdminFormState, Action> } = {

  [ACTION.RECEIVE_LANGUAGE_OPTIONS]: (state: ExternalUrlAdminFormState,
    action: ReceiveLanguageOptions): ExternalUrlAdminFormState => {

    return {
      ...state,
      languageOptions: action.options
    };
  },

  [ACTION.REQUEST_EXTERNAL_URL]: (state: ExternalUrlAdminFormState,
    action: RequestExternalUrl): ExternalUrlAdminFormState => {

    return {
      ...state,
      externalUrlItem: undefined,
      originalExternalUrlItem: undefined,
      fetching: true,
      saving: false,
      validation: {}
    };
  },

  [ACTION.RECEIVE_EXTERNAL_URL]: (state: ExternalUrlAdminFormState,
    action: ReceiveExternalUrl): ExternalUrlAdminFormState => {

    return {
      ...state,
      externalUrlItem: action.item,
      originalExternalUrlItem: action.item,
      fetching: false
    };
  },

  [ACTION.FAIL_EXTERNAL_URL]: (state: ExternalUrlAdminFormState,
    action: FailExternalUrl): ExternalUrlAdminFormState => {

    return {
      ...state,
      fetching: false
    };
  },

  [ACTION.REQUEST_DELETE_EXTERNAL_URL]: (state: ExternalUrlAdminFormState,
    action: RequestDeleteExternalUrl): ExternalUrlAdminFormState => {

    return {
      ...state,
      deleting: true
    };
  },

  [ACTION.RECEIVE_DELETE_EXTERNAL_URL]: (state: ExternalUrlAdminFormState,
    action: ReceiveDeleteExternalUrl): ExternalUrlAdminFormState => {

    return {
      ...state,
      deleting: false
    };
  },

  [ACTION.FAIL_DELETE_EXTERNAL_URL]: (state: ExternalUrlAdminFormState,
    action: FailDeleteExternalUrl): ExternalUrlAdminFormState => {

    return {
      ...state,
      deleting: false
    };
  },

  [ACTION.REQUEST_SAVE_EXTERNAL_URL]: (state: ExternalUrlAdminFormState,
    action: RequestSaveExternalUrl): ExternalUrlAdminFormState => {

    return {
      ...state,
      saving: true
    };
  },

  [ACTION.RECEIVE_SAVE_EXTERNAL_URL]: (state: ExternalUrlAdminFormState,
    action: ReceiveSaveExternalUrl): ExternalUrlAdminFormState => {

    return {
      ...state,
      saving: false,
      originalExternalUrlItem: state.externalUrlItem
    };
  },

  [ACTION.FAIL_SAVE_EXTERNAL_URL]: (state: ExternalUrlAdminFormState,
    action: FailSaveExternalUrl): ExternalUrlAdminFormState => {

    return {
      ...state,
      saving: false
    };
  },

  [ACTION.UPDATE_EXCLUDED_FIELDS]: (state: ExternalUrlAdminFormState, action: UpdateExcludedFields): ExternalUrlAdminFormState => {
    return {
      ...state,
      externalUrlItem: {
        ...state.externalUrlItem,
        excludeFields: action.excludedFields
      }
    };
  },

  [ACTION.CHANGE_LANGUAGE]: (state: ExternalUrlAdminFormState,
    action: ChangeLanguage): ExternalUrlAdminFormState => {

    const externalUrlItem = state.externalUrlItem || {} as ExternalUrlItem;
    const language = externalUrlItem.language || [];

    return {
      ...state,
      externalUrlItem: {
        ...externalUrlItem,
        language: language.some(languageItem => languageItem.value === action.languageId)
          ? language.map(
            languageItem => languageItem.value === action.languageId
              ? {
                ...languageItem,
                description: action.description
              }
              : languageItem
          )
          : [
            ...language,
            {
              value: action.languageId,
              description: action.description
            }
          ]
      },
      validation: clearValidationForSingleParameter(state.validation, 'language', languageValidationParameter, action.languageId)
    };
  },

  [ACTION.COPY_LANGUAGE_TO_ALL]: (state: ExternalUrlAdminFormState,
    action: CopyLanguageToAll): ExternalUrlAdminFormState => {

    const externalUrlItem = state.externalUrlItem || {} as ExternalUrlItem;
    const language = externalUrlItem.language || [];
    const targetLanguage = language.find(languageItem => languageItem.value === action.languageId);

    if (targetLanguage === undefined) {
      return state;
    }

    const updatedLanguage = language.map(
      languageItem => ({
        ...languageItem,
        description: targetLanguage.description
      })
    );
    const newLanguage = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !language.some(languageItem => languageItem.value === languageId))
      .map(languageId => ({
        value: languageId,
        description: targetLanguage.description
      }));

    return {
      ...state,
      externalUrlItem: {
        ...externalUrlItem,
        language: [
          ...updatedLanguage,
          ...newLanguage
        ]
      },
      validation: clearValidationForAllParameters(state.validation, 'language', languageValidationParameter)
    };
  },

  [ACTION.CHANGE_LONG_DESCRIPTION]: (state: ExternalUrlAdminFormState,
    action: ChangeLongDescription): ExternalUrlAdminFormState => {

    const externalUrlItem = state.externalUrlItem || {} as ExternalUrlItem;
    const longDescription = externalUrlItem.longDescription || [];

    return {
      ...state,
      externalUrlItem: {
        ...externalUrlItem,
        longDescription: longDescription.some(longDescriptionItem => longDescriptionItem.value === action.languageId)
          ? longDescription.map(
            longDescriptionItem => longDescriptionItem.value === action.languageId
              ? {
                ...longDescriptionItem,
                description: action.description
              }
              : longDescriptionItem
          )
          : [
            ...longDescription,
            {
              value: action.languageId,
              description: action.description
            }
          ]
      },
      validation: clearValidationForSingleParameter(
        state.validation,
        'longDescription',
        longDescriptionValidationParameter,
        action.languageId
      )
    };
  },

  [ACTION.COPY_LONG_DESCRIPTION_TO_ALL]: (state: ExternalUrlAdminFormState,
    action: CopyLongDescriptionToAll): ExternalUrlAdminFormState => {

      const externalUrlItem = state.externalUrlItem || {} as ExternalUrlItem;
      const longDescription = externalUrlItem.longDescription || [];
      const targetLongDescription = longDescription.find(longDescriptionItem => longDescriptionItem.value === action.languageId);
  
      if (targetLongDescription === undefined) {
        return state;
      }
  
      const updatedlongDescription = longDescription.map(
        languageItem => ({
          ...languageItem,
          description: targetLongDescription.description
        })
      );
      const newlongDescription = (state.languageOptions || [])
        .map(option => option.value)
        .filter(languageId => !longDescription.some(languageItem => languageItem.value === languageId))
        .map(languageId => ({
          value: languageId,
          description: targetLongDescription.description
        }));
  
      return {
        ...state,
        externalUrlItem: {
          ...externalUrlItem,
          longDescription: [
            ...updatedlongDescription,
            ...newlongDescription
          ]
        },
        validation: clearValidationForAllParameters(state.validation, 'longDescription', longDescriptionValidationParameter)
      };
  },

  [ACTION.UPDATE_ITEM]: (state: ExternalUrlAdminFormState,
    action: UpdateItem): ExternalUrlAdminFormState => {

    const externalUrlItem = {
      ...(state.externalUrlItem || {}),
      ...action.item
    } as ExternalUrlItem;

    const validation = clearValidationForKeys(state.validation, Object.keys(action.item) as any[]);

    return {
      ...state,
      externalUrlItem: externalUrlItem,
      validation
    };
  },

  [ACTION.CLEAR_FORM]: (state: ExternalUrlAdminFormState,
    action: ClearForm): ExternalUrlAdminFormState => {

    return {
      ...state,
      ...INITIAL_STATE,
      languageOptions: state.languageOptions
    };
  },

  [ACTION.UPDATE_VALIDATION]: (state: ExternalUrlAdminFormState,
    action: UpdateValidation): ExternalUrlAdminFormState => {
    return {
      ...state,
      validation: { ...state.validation, ...action.validation }
    };
  },

  [ACTION.RESET_VALIDATION]: (state: ExternalUrlAdminFormState, action: ResetValidation): ExternalUrlAdminFormState => {
    return {
      ...state,
      validation: { ...state.validation, [action.key]: { messages: [] } }
    };
  }
};

const stateReducer: Reducer<ExternalUrlAdminFormState> = (
  state: ExternalUrlAdminFormState = INITIAL_STATE, action: Action) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};

export const reducer = withOptionsReducer(STATE.externalUrlAdministration, stateReducer);
